import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import Logo, { LogoVariant } from './Logo';
import { useIsMobile } from '@/hooks/use-mobile';
import ThemeSwitcher from './ThemeSwitcher';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navigation from './header/Navigation';
import MobileMenu from './header/MobileMenu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [logoVariant, setLogoVariant] = useState<LogoVariant>('code');
  const isMobile = useIsMobile();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleScroll = () => {
    if (window.scrollY > 10) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
    
    if (isHomePage) {
      const sections = ['about', 'skills', 'experience', 'blog', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          } else if (window.scrollY < 300) {
            setActiveSection('');
          }
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!isHomePage && href.startsWith('#')) {
      return; // Don't intercept navigation if we're not on the home page
    }
    
    e.preventDefault();
    
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    
    if (href.startsWith('#')) {
      const targetElement = document.querySelector(href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState(null, '', href);
        setActiveSection(href.substring(1));
      }
    }
  };

  const handleLogoChange = (newVariant: LogoVariant) => {
    setLogoVariant(newVariant);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 shadow-sm backdrop-blur-sm py-4' : 'py-6 pt-4 sm:py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center">
        <Link 
          to="/" 
          className="text-xl font-semibold flex items-center gap-1 sm:gap-2 text-primary hover:text-primary/80 transition-colors hover-tilt"
          title="Click to change logo icon"
        >
          <Logo 
            variant={logoVariant}
            showText={!isMobile}
            autoFlip={true}
            flipInterval={5000}
          />
          {isMobile && <span className="text-sm font-mono">Aniket.eng</span>}
        </Link>

        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          
          <Navigation 
            activeSection={activeSection}
            isHomePage={isHomePage}
            handleNavLinkClick={handleNavLinkClick}
          />

          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden hover-tilt" 
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </div>

      <MobileMenu 
        isOpen={isMobileMenuOpen}
        activeSection={activeSection}
        isHomePage={isHomePage}
        handleNavLinkClick={handleNavLinkClick}
      />
    </header>
  );
};

export default Header;
